import * as React from "react";
import { useSearchParams } from "react-router";
import {
  Building2,
  Check,
  KeyRound,
  MapPin,
  Moon,
  ShieldCheck,
  Sun,
  Trash2,
  User,
} from "lucide-react";
import {
  DELETE_ACCOUNT_EFFECTS,
  DELETE_ACCOUNT_PHRASE,
  ROLE_LABELS,
  TOTP_CODE_LENGTH,
} from "@taskiq/shared";
import { CodeInput } from "@/components/auth/CodeInput";
import { PersonAvatar } from "@/components/ui/Avatar";
import { PhoneField } from "@/components/ui/PhoneField";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { useConfirm, useDiscardGuard } from "@/components/ui/confirm-context";
import { FormMessage } from "@/components/ui/Feedback";
import { Field, Form, Input, PasswordInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/toast-context";
import { TimezoneSelect } from "@/components/ui/TimezoneSelect";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { describeAuthError } from "@/lib/auth-errors";
import { cn } from "@/lib/cn";
import { useMfa } from "@/lib/mfa";
import { useTheme, type ThemeChoice } from "@/lib/theme";
import { PLACEHOLDER } from "@/lib/placeholders";

/**
 * The signed-in person's own account.
 *
 * Reached from the rail, which is where a name in a corner sends people looking.
 *
 * ── One page, two dialogs ───────────────────────────────────────────────────
 *
 * Security used to be a second section in the mini nav, holding a permanently
 * open password form and a permanently open enrolment panel. That is two forms
 * nobody is filling in, occupying a screen somebody opened to change their phone
 * number - and a QR code sitting there at all times, on a page a colleague can
 * see over your shoulder, for a factor you set up once.
 *
 * Both are dialogs now, behind a button that says what it does. The page is a
 * description of the account; the dialogs are the two places anything changes.
 *
 * ── A mini nav, not tabs ────────────────────────────────────────────────────
 *
 * `role="tablist"` promises a tabpanel and arrow-key navigation between panels
 * that are all present in the document. These are separate panels rendered one
 * at a time and reflected in the URL, which is a small router, not a tab widget -
 * so they are plain links, announced as links, and each one is bookmarkable.
 * Same reasoning as the Team page's filter row.
 *
 * `AccountIdentity` renders at the top of the panel on every section rather
 * than inside one of them - it is who the account belongs to, not a setting,
 * so it stays on screen while "Profile" and "Sign-in and security" answer
 * different questions underneath it.
 */

type Section = "profile" | "security";

const SECTIONS: { id: Section; label: string; icon: React.ReactNode }[] = [
  { id: "profile", label: "Profile", icon: <User /> },
  { id: "security", label: "Sign-in and security", icon: <ShieldCheck /> },
];

function isSection(value: string | null): value is Section {
  return SECTIONS.some((section) => section.id === value);
}

export function Profile() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const raw = params.get("tab");
  const section: Section = isSection(raw) ? raw : "profile";

  if (!user) return null;

  return (
    <div className="page">
      <PageHeader title="Your account" />

      <div className="subnav-layout">
        <nav className="subnav" aria-label="Account sections">
          {SECTIONS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-current={entry.id === section ? "page" : undefined}
              onClick={() => setParams(entry.id === "profile" ? {} : { tab: entry.id })}
              className={cn("subnav__link", entry.id === section && "subnav__link--active")}
            >
              {entry.icon}
              {entry.label}
            </button>
          ))}
        </nav>

        <div className="subnav-layout__panel">
          <div className="stack">
            <AccountIdentity />
            {section === "profile" ? (
              <>
                <ProfileForm />
                <PreferencesCard />
              </>
            ) : (
              <SecurityCard />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────── identity ─────────────────────────────── */

function AccountIdentity() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    // `on-panel` is not decoration: it swaps the focus ring and the selection
    // colours, both of which are close to invisible against near-black.
    <div className="on-panel account-hero">
      <PersonAvatar person={user} tone="panel" size="lg" />
      <div className="account-hero__text">
        <div className="account-hero__name">
          {user.full_name ?? user.email}
          <Badge tone="accent">{ROLE_LABELS[user.role]}</Badge>
        </div>
        <p className="account-hero__email">{user.email}</p>
        <div className="account-hero__chips">
          <span className="chip">
            <Building2 />
            {user.company.name}
          </span>
          <span className="chip">
            <MapPin />
            {user.site.name}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── personal details ───────────────────────── */

function ProfileForm() {
  const { user, refresh } = useAuth();

  const initial = React.useMemo(
    () => ({
      full_name: user?.full_name ?? "",
      phone: user?.phone ?? "",
      timezone: user?.timezone ?? "UTC",
    }),
    [user]
  );

  const [form, setForm] = React.useState(initial);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const toast = useToast();

  // Re-seed when the profile arrives or changes underneath the form, but never
  // while it is being edited - that would discard what somebody is typing.
  React.useEffect(() => {
    setForm(initial);
  }, [initial]);

  if (!user) return null;

  const patch: Record<string, unknown> = {};
  if (form.full_name.trim() !== initial.full_name) patch.full_name = form.full_name.trim();
  if (form.timezone.trim() !== initial.timezone) patch.timezone = form.timezone.trim();
  if (form.phone.trim() !== initial.phone) patch.phone = form.phone.trim() || null;

  const dirty = Object.keys(patch).length > 0;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await api("/auth/me", { method: "PATCH", body: patch });
      // The rail renders the name from the session, so it has to be re-read or
      // the page saves a change the chrome keeps disagreeing with.
      await refresh();
      toast.success("Profile saved");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save your profile"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader title="Personal details" />
      <CardBody>
        <Form onSubmit={submit}>
          {/* Two columns, and the reason is the measure rather than the pixels
              saved. A 40px text field stretched across 1100px of console gives a
              three-word answer the same visual weight as a paragraph, and leaves
              the eye travelling the width of the screen between a label and the
              caret it is meant to land on. */}
          <div className="form-grid">
            <Field label="Full name" htmlFor="profile-name" required>
              <Input
                id="profile-name"
                placeholder={PLACEHOLDER.fullName}
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              />
            </Field>

            <Field
              label="Phone"
              htmlFor="profile-phone"
              hint="Optional. Used when a stop-work needs escalating to you."
            >
              <PhoneField
                id="profile-phone"
                value={form.phone}
                onChange={(phone) => setForm((f) => ({ ...f, phone }))}
              />
            </Field>

            <Field
              label="Timezone"
              htmlFor="profile-tz"
              hint="What your timestamps and digests are shown in."
            >
              <TimezoneSelect
                id="profile-tz"
                value={form.timezone}
                onChange={(timezone) => setForm((f) => ({ ...f, timezone }))}
              />
            </Field>
          </div>

          <FormMessage>{error}</FormMessage>

          {/* Only once there is something to save. A permanently enabled Save
              on a form nobody has touched is a button that does nothing. */}
          <div className="form-actions">
            <Button type="submit" size="sm" loading={saving} disabled={!dirty}>
              Save changes
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
}

/* ────────────────────────────── preferences ───────────────────────────── */

/**
 * The UI languages that are actually translated.
 *
 * `native` is what the speaker calls it and `english` is what the reader looking
 * over their shoulder calls it, which is why both are shown: somebody who has
 * landed on a console in a language they cannot read needs to find their own
 * entry by its native name, and the administrator helping them needs the English
 * one. `code` is the two-letter badge, and it is what makes the row scannable
 * once the list is nine long.
 *
 * One entry today, and the control is offered anyway rather than held back for
 * the second: its absence reads as "English only, forever" where a list with one
 * option in it reads as "English only, for now". **Nothing consumes this yet** -
 * there is no i18n layer, so the stored value is a recorded preference and not a
 * working switch. Add a locale here and wire the reader, in that order.
 */
const LANGUAGES: { value: string; code: string; native: string; english: string }[] = [
  { value: "en", code: "EN", native: "English", english: "English" },
];

const THEMES: { value: ThemeChoice; label: string; hint: string; icon: React.ReactNode }[] = [
  { value: "light", label: "Light", hint: "Readable in sunlight", icon: <Sun /> },
  { value: "dark", label: "Dark", hint: "Easier in a dim room", icon: <Moon /> },
];

const LANGUAGE_STORAGE_KEY = "taskiq.language";

/**
 * How the console looks and reads, for this person.
 *
 * ── Cards, not a dropdown and not a switch ─────────────────────────────────
 *
 * Both options are laid out as a grid of buttons the reader can see the whole of
 * at once, which is the shape the main product's settings screen uses for the
 * same two questions. Two reasons it is right here rather than merely
 * consistent. A theme is chosen by **looking** at the two words and imagining
 * the screen, so hiding one of them behind a dropdown makes the reader open it
 * to remember what the alternative was. And the language list is about to be
 * six or nine long, in scripts the reader may not be able to skim - a native
 * name, an English name and a two-letter badge on a card are all findable, where
 * the same three facts crammed into one line of a `<select>` are not.
 *
 * The check mark is not decoration. `aria-pressed` says which is chosen to a
 * screen reader; the tick and the tinted border say it to everyone else, and a
 * border tint on its own is the kind of state that disappears at a glance in
 * bright light - which is the condition this product is designed around.
 *
 * ── Nothing here is saved to the API, and that is deliberate ───────────────
 *
 * Both values live in `localStorage` and apply the moment they change, so there
 * is no Save button and no dirty state. That is not laziness about the API: the
 * theme is stamped onto `<html>` by a script in `index.html` **before the first
 * paint**, which is what stops a light flash on a dark-themed account, and a
 * value that only exists on the server is not readable at that point. A round
 * trip would buy a preference that follows you between browsers at the cost of a
 * flash on every cold load, which is the wrong trade for a switch flipped once.
 *
 * The consequence is per-browser preferences: a new laptop starts from the
 * defaults again.
 */
function PreferencesCard() {
  const { theme, setTheme } = useTheme();
  const [language, setLanguage] = React.useState(() => {
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      return LANGUAGES.some((entry) => entry.value === stored) ? stored! : "en";
    } catch {
      // Private mode, or storage disabled by policy. Same fallback as lib/theme.
      return "en";
    }
  });

  function chooseLanguage(next: string) {
    setLanguage(next);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    } catch {
      /* see above */
    }
  }

  return (
    <Card>
      <CardHeader title="Preferences" />

      <CardBody>
        <div className="stack">
          <fieldset className="pref-set">
            <legend className="type-label pref-set__legend">Language</legend>
            <div className="pref-grid">
              {LANGUAGES.map((entry) => (
                <PrefOption
                  key={entry.value}
                  active={language === entry.value}
                  badge={entry.code}
                  title={entry.native}
                  hint={entry.english}
                  onClick={() => chooseLanguage(entry.value)}
                />
              ))}
            </div>
          </fieldset>

          <fieldset className="pref-set">
            <legend className="type-label pref-set__legend">Theme</legend>
            <div className="pref-grid">
              {THEMES.map((entry) => (
                <PrefOption
                  key={entry.value}
                  active={theme === entry.value}
                  badge={entry.icon}
                  title={entry.label}
                  hint={entry.hint}
                  onClick={() => setTheme(entry.value)}
                />
              ))}
            </div>
          </fieldset>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * One card in a preference grid.
 *
 * A `<button>` rather than a radio, and `aria-pressed` rather than `aria-checked`
 * - these act on click instead of staging a value for a submit, which is the same
 * distinction `Switch` is written around. A radio group would also promise arrow-
 * key navigation between the options, and there is nothing here to arrow between
 * that Tab does not already reach.
 */
function PrefOption({
  active,
  badge,
  title,
  hint,
  onClick,
}: {
  active: boolean;
  badge: React.ReactNode;
  title: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn("pref-opt", active && "pref-opt--active")}
    >
      <span className="pref-opt__badge">{badge}</span>
      <span className="pref-opt__text">
        <span className="pref-opt__title">{title}</span>
        <span className="pref-opt__hint">{hint}</span>
      </span>
      {active && <Check className="pref-opt__check" aria-hidden />}
    </button>
  );
}

/* ─────────────────────────────── security ─────────────────────────────── */

function SecurityCard() {
  // One `useMfa()` for the card and the dialog it owns. Calling the hook in both
  // would give each its own factor list and its own listFactors on mount, and
  // the dialog's copy would be the one that never gets refreshed.
  const mfa = useMfa();
  const { factors, refreshFactors, disableTotp } = mfa;
  const confirm = useConfirm();
  const toast = useToast();

  const [changingPassword, setChangingPassword] = React.useState(false);
  const [enrolling, setEnrolling] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const enabled = factors.length > 0;

  /**
   * Turning a factor off is the one irreversible half - the secret is gone and
   * the authenticator entry it came from is dead, so coming back means scanning
   * a new code. Turning it on is not asked about, because it is one click to
   * undo and a confirmation on something harmless is how people learn to click
   * through the one that matters.
   */
  async function turnOff() {
    const ok = await confirm({
      title: "Turn off two-step verification?",
      message:
        "Your password alone will be enough to sign in. The code in your authenticator app stops working, so turning it back on means scanning a new one.",
      confirmLabel: "Turn it off",
      cancelLabel: "Keep it on",
      danger: true,
    });
    if (!ok) return;

    setError(null);
    setBusy(true);
    try {
      for (const factor of factors) await disableTotp(factor.id);
      await refreshFactors();
      toast.success("Two-step verification is off", {
        description: "Your password alone now signs you in.",
      });
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader title="Sign-in and security" />

        <ul className="setting-list">
          <li className="setting">
            <div className="setting__text">
              <p className="setting__title">Password</p>
              <p className="setting__body">
                Changing it does not sign you out of your other devices.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              icon={<KeyRound />}
              onClick={() => setChangingPassword(true)}
            >
              Change password
            </Button>
          </li>

          <li className="setting">
            <div className="setting__text">
              <p className="setting__title">
                Two-step verification (2FA)
                {enabled ? (
                  <Badge tone="ok" size="sm">
                    On
                  </Badge>
                ) : (
                  <Badge tone="neutral" size="sm">
                    Off
                  </Badge>
                )}
              </p>
              <p className="setting__body">
                {enabled
                  ? "A code from your authenticator app is asked for after your password."
                  : "Your password alone is enough to sign in to this account today."}
              </p>
              <FormMessage>{error}</FormMessage>
            </div>

            {enabled ? (
              <Button variant="secondary" size="sm" loading={busy} onClick={() => void turnOff()}>
                Disable 2FA
              </Button>
            ) : (
              <Button size="sm" icon={<ShieldCheck />} onClick={() => setEnrolling(true)}>
                Enable 2FA
              </Button>
            )}
          </li>
        </ul>
      </Card>

      <DeleteAccountCard />

      <PasswordDialog open={changingPassword} onClose={() => setChangingPassword(false)} />
      <TwoFactorDialog
        mfa={mfa}
        open={enrolling}
        onClose={() => setEnrolling(false)}
        onEnrolled={() => {
          setEnrolling(false);
          void refreshFactors();
        }}
      />
    </>
  );
}

/* ──────────────────────────── delete account ──────────────────────────── */

/**
 * `DELETE /auth/me`. App Store guideline 5.1.1(v) requires this to be reachable
 * from inside the app that offers sign-up, and the parity rule in
 * docs/conventions.md then requires it here too - a person who deletes their
 * account on the phone must be able to find the same thing on the web, in the
 * same place, saying the same words.
 *
 * ── Its own card, below the settings list ───────────────────────────────────
 *
 * Not a fourth `<li class="setting">` beside Password and 2FA. Those are
 * settings: things you change, and change back. This ends the account, and a
 * row in a list of toggles is read at the speed of a list of toggles. Last on
 * the page, in its own bordered card, because that is where a destructive
 * action belongs and where people have learned to look for one.
 */
function DeleteAccountCard() {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Card className="card--danger">
        <CardHeader title="Delete your account" />
        <CardBody>
          <p className="prose prose--sm">
            Permanently removes your sign-in and your personal details. This cannot be undone, by
            you or by us. Your employer's safety records stay, no longer showing your name.
          </p>
          <div className="card-actions">
            <Button variant="danger" size="sm" icon={<Trash2 />} onClick={() => setOpen(true)}>
              Delete account
            </Button>
          </div>
        </CardBody>
      </Card>

      <DeleteAccountDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function DeleteAccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { signOut } = useAuth();
  const formId = React.useId();

  const [typed, setTyped] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  // Case-sensitive, and the shared constant rather than a literal - the phone
  // asks for the same word out of the same export.
  const confirmed = typed === DELETE_ACCOUNT_PHRASE;

  // No `useDiscardGuard` here, unlike every other dialog on this page. Those
  // protect work in progress; the only thing typed here is the word "DELETE",
  // which costs six keystrokes to redo. Asking "are you sure you want to
  // discard?" on the way *out* of a deletion dialog is a confirmation pointed
  // at the wrong outcome.
  function close() {
    if (deleting) return;
    setTyped("");
    setError(null);
    onClose();
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!confirmed) return;

    setError(null);
    setDeleting(true);
    try {
      await api("/auth/me", { method: "DELETE" });

      /**
       * Nothing to route to. The profile is a tombstone and the identity is
       * gone, so every subsequent request 401s and any React state still on
       * screen describes an account that no longer exists.
       *
       * `signOut` is best-effort for exactly that reason - GoTrue may refuse to
       * log out an identity it has already deleted, and a thrown error there
       * must not leave the person staring at a console they can no longer use.
       * The hard navigation is what actually guarantees a clean slate: it
       * discards every provider, cache and timer in the tab.
       */
      try {
        await signOut();
      } catch {
        /* the identity is already gone; the reload below is the real sign-out */
      }
      window.location.replace("/");
    } catch (err) {
      // The last-superadmin refusal arrives here as a 400 carrying the sentence
      // the database wrote. Shown as-is: it names the fix.
      setError(apiErrorMessage(err, "Your account could not be deleted."));
      setDeleting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Delete your account"
      className="dialog--narrow"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={close} disabled={deleting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="danger"
            size="sm"
            icon={<Trash2 />}
            loading={deleting}
            disabled={!confirmed}
          >
            Delete my account
          </Button>
        </>
      }
    >
      <Form id={formId} onSubmit={submit}>
        <p className="prose prose--sm">
          <strong>This cannot be undone.</strong> Neither you nor Visionify can restore the account
          afterwards.
        </p>

        <div className="consequences">
          <p className="setting__title">What is deleted</p>
          <ul className="consequences__list">
            {DELETE_ACCOUNT_EFFECTS.removed.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        <div className="consequences">
          <p className="setting__title">What is kept</p>
          <ul className="consequences__list">
            {DELETE_ACCOUNT_EFFECTS.retained.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        <Field
          label={`Type ${DELETE_ACCOUNT_PHRASE} to confirm`}
          htmlFor="delete-confirm"
          required
          hint="In capitals, exactly as shown."
        >
          <Input
            id="delete-confirm"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
        </Field>

        <FormMessage>{error}</FormMessage>
      </Form>
    </Modal>
  );
}

/* ──────────────────────────── change password ─────────────────────────── */

/**
 * The two password fields stay stacked while everything else on this page went
 * to two columns, and that is not an oversight. A password and its confirmation
 * are read against each other character by character; side by side in a 26rem
 * dialog each one is about 12rem wide, which is where a long passphrase starts
 * scrolling inside its own field at exactly the moment somebody is checking it
 * matches the one beside it.
 */
function PasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { updatePassword } = useAuth();
  const toast = useToast();
  const formId = React.useId();

  const [current, setCurrent] = React.useState("");
  const [next, setNext] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const meetsLength = next.length >= 10;
  const matches = next.length > 0 && next === confirm;

  // Nothing typed here should survive being closed and reopened - a password
  // left in state is a password sitting in a heap dump for the session.
  const discard = React.useCallback(() => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError(null);
    onClose();
  }, [onClose]);

  // Three typed secrets and no guard, where the dialogs that edit a *record*
  // have had one for a while. A password typed carefully and lost to a stray
  // Escape is retyped from scratch, which is the same cost the guard exists to
  // prevent everywhere else.
  const close = useDiscardGuard(current !== "" || next !== "" || confirm !== "", saving, discard);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (!meetsLength) {
      setError("Use at least 10 characters.");
      return;
    }
    if (!matches) {
      setError("The two passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      // This project requires the current password on a change - GoTrue refuses
      // the request without one rather than the console enforcing it locally, so
      // a wrong value comes back as a server error the field shows inline.
      await updatePassword(next, current);
      toast.success("Password changed", {
        description: "Use the new one next time you sign in.",
      });
      discard();
    } catch (err) {
      // `secure_password_change` is also on for this project, so GoTrue refuses
      // a change made from a session that has been open a long time. That reads
      // as a mysterious failure unless the copy says what to do about it.
      setError(describeAuthError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => void close()}
      title="Change password"
      className="dialog--narrow"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => void close()} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            size="sm"
            icon={<KeyRound />}
            loading={saving}
            disabled={!current || !meetsLength || !matches}
          >
            Update password
          </Button>
        </>
      }
    >
      <Form id={formId} onSubmit={submit}>
        <p className="prose prose--sm">This does not sign you out of your other devices.</p>

        <Field label="Current password" htmlFor="current-password" required>
          <PasswordInput
            id="current-password"
            autoComplete="current-password"
            autoFocus
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>

        <Field label="New password" htmlFor="new-password" required hint="At least 10 characters.">
          <PasswordInput
            id="new-password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>

        <Field
          label="Confirm new password"
          htmlFor="confirm-password"
          required
          error={confirm.length > 0 && !matches ? "These do not match." : undefined}
        >
          <PasswordInput
            id="confirm-password"
            autoComplete="new-password"
            invalid={confirm.length > 0 && !matches}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>

        <FormMessage>{error}</FormMessage>
      </Form>
    </Modal>
  );
}

/* ──────────────────────────── two-factor setup ────────────────────────── */

/**
 * TOTP enrolment, through Supabase rather than a secret column of ours.
 *
 * ── The enrolment order is the security property ────────────────────────────
 *
 * `enroll` creates the factor **unverified** and hands back a QR code. It only
 * becomes real when a code generated from it is accepted, which is what proves
 * the secret actually reached an authenticator app. Skipping the verify step
 * would lock people out of their own accounts with a secret they never scanned.
 *
 * An abandoned enrolment leaves an unverified factor behind, so this cleans one
 * up on every way out - Cancel, the close button, Escape and the backdrop, all
 * of which `Modal` funnels into `onClose`. Left there, `listFactors()` starts
 * returning two the next time and `verifyMfa` picks the wrong one.
 *
 * ── No QR dependency ────────────────────────────────────────────────────────
 *
 * `enroll()` returns `totp.qr_code` already rendered, as an SVG data URL. The
 * setup key is offered alongside it because a phone cannot scan a QR code shown
 * on that same phone.
 */
function TwoFactorDialog({
  mfa,
  open,
  onClose,
  onEnrolled,
}: {
  mfa: ReturnType<typeof useMfa>;
  open: boolean;
  onClose: () => void;
  onEnrolled: () => void;
}) {
  const { enrollTotp, confirmTotp, disableTotp } = mfa;
  const toast = useToast();
  const formId = React.useId();

  const [setup, setSetup] = React.useState<{
    factorId: string;
    qr: string;
    secret: string;
  } | null>(null);
  const [showSecret, setShowSecret] = React.useState(false);
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  /**
   * The QR is minted when the dialog opens, not when the page loads. A secret
   * fetched for a panel nobody opened is a factor row created for nothing, and
   * `enroll` names each one uniquely, so the abandoned ones accumulate.
   */
  React.useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setBusy(true);
    setError(null);

    enrollTotp()
      .then((result) => {
        if (!cancelled) setSetup(result);
        // Closed while the request was in flight: the factor exists on the
        // server and nothing on screen will ever reference it again.
        else void disableTotp(result.factorId).catch(() => undefined);
      })
      .catch((err) => {
        if (!cancelled) setError(describeAuthError(err));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, enrollTotp, disableTotp]);

  const discard = React.useCallback(() => {
    setSetup(null);
    setCode("");
    setShowSecret(false);
    setError(null);
    onClose();
    if (setup) void disableTotp(setup.factorId).catch(() => undefined);
  }, [setup, disableTotp, onClose]);

  /**
   * Leaving before the code is accepted throws away a factor that already exists
   * on the server, and the reader has usually just scanned it into an app - so
   * the entry they added is about to stop working and nothing would have said
   * so. The phone asks the same question for the same reason.
   */
  //
  // `busy` is deliberately passed as false. It covers both the enrolment request
  // and the verify, and blocking the close during the first would leave the X
  // inert for the half-second the QR takes to arrive - the effect above already
  // tears down a factor that lands after the dialog has gone.
  const close = useDiscardGuard(setup !== null, false, discard);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!setup) return;

    setError(null);
    setBusy(true);
    try {
      await confirmTotp(setup.factorId, code);
      // Verified, so it must not be torn down on the way out.
      setSetup(null);
      setCode("");
      setShowSecret(false);
      toast.success("Two-step verification is on", {
        description: "You will be asked for a code from your authenticator app at every sign-in.",
      });
      onEnrolled();
    } catch (err) {
      setError(describeAuthError(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => void close()}
      title="Enable 2FA"
      className="dialog--narrow"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => void close()}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            size="sm"
            loading={busy}
            disabled={!setup || code.length < TOTP_CODE_LENGTH}
          >
            Turn on
          </Button>
        </>
      }
    >
      <Form id={formId} onSubmit={submit}>
        <p className="prose prose--sm">
          A code from an authenticator app, every time you sign in with a password. Scan this with
          Google Authenticator, 1Password, or any TOTP app, then enter the code it shows.
        </p>

        <div className="qr">
          {setup ? (
            <img src={setup.qr} alt="" className="qr__image" />
          ) : (
            <div className="qr__image qr__image--pending" />
          )}

          <div className="qr__aside">
            <button
              type="button"
              className="text-link text-link--accent"
              disabled={!setup}
              onClick={() => setShowSecret((v) => !v)}
            >
              {showSecret ? "Hide setup key" : "Can't scan it?"}
            </button>
            {showSecret && setup && (
              <>
                <p className="qr__hint">Enter this key in your app by hand.</p>
                <code className="qr__secret">{setup.secret}</code>
              </>
            )}
          </div>
        </div>

        <Field label="Code from your app" htmlFor="totp-code" required>
          <CodeInput
            id="totp-code"
            size="sm"
            value={code}
            onChange={setCode}
            invalid={Boolean(error)}
            autoFocus
          />
        </Field>

        <FormMessage>{error}</FormMessage>
      </Form>
    </Modal>
  );
}
